/**
 * Looks laid over the finished frame (05 §9.4, 08 accessibility). Every one of them is applied
 * AFTER the palette lock, like haze and the grade, so none can make a static pixel cross a
 * dither threshold while the camera scrolls (the M0 measurement). All default to off / neutral:
 * with `NEUTRAL_OPTIONS` the pipeline draws exactly what it drew before these existed.
 */

import core from '../../../content/palettes/core.json';
import { getGrade, type Grade } from '../palette';

export interface PipelineOptions {
  /**
   * Night-vision goggles (gear, 04 §6): the frame as the tube shows it - green phosphor in a few
   * steps, shadows lifted so the dark gives up what is in it, a soft round vignette. It replaces
   * the night grade rather than sitting on top of it. Meant for night and dusk; the caller decides.
   */
  nvg: boolean;
  /**
   * How much of the night the ranger suffers (the Night Eyes perk, `SimMods.nightPenalty`):
   * 1 = the night grade as authored, 0.5 = halfway to the dusk grade, 0 = dusk. Night only.
   */
  nightPenalty: number;
  /** Scanlines locked to the display's integer scale (so they cannot moire) and a little more vignette. */
  crt: boolean;
  /**
   * Photosensitivity: no pixel may get brighter than the brightest thing near it a frame ago by
   * more than `FLASH_CAP_STEP`, so a flash swells over several frames instead of arriving in one.
   * Pair it with `Effects.setOptions({ flashes: false })`, which softens flashes at their source.
   */
  flashCap: boolean;
}

export const NEUTRAL_OPTIONS: Readonly<PipelineOptions> = { nvg: false, nightPenalty: 1, crt: false, flashCap: false };

/** Largest rise in a pixel's luminance (sRGB, 0..1) from one frame to the next while `flashCap` is on. */
export const FLASH_CAP_STEP = 0.12;
/** Steps of phosphor brightness under NVG: few enough to stay a palette. */
export const NVG_STEPS = 7;
/** Display rows the gap between two scanlines is darkened to, by integer scale (index = scale, capped). */
const SCANLINE_GAP = [1, 1, 0.78, 0.64, 0.6] as const;

/** The NVG tube's colour comes from the palette like every other colour (05 §4). */
export const phosphorHex = (): string => core.ramps.phosphor[0] ?? '#ffffff';

const mix = (a: number, b: number, t: number): number => a + (b - a) * t;

function mixHex(a: string, b: string, t: number): string {
  const ch = (hex: string, i: number): number => Number.parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
  const out = [0, 1, 2].map((i) => Math.round(mix(ch(a, i), ch(b, i), t)).toString(16).padStart(2, '0'));
  return `#${out.join('')}`;
}

/** The grade the post pass should apply: at night, `nightPenalty` of the way from dusk to the night grade. */
export function effectiveGrade(grade: Grade, options: Pick<PipelineOptions, 'nightPenalty'>): Grade {
  const k = Math.max(0, Math.min(1, options.nightPenalty));
  if (grade.name !== 'night' || k >= 1) return grade;
  const dusk = getGrade('dusk');
  return {
    ...grade,
    hazeAmount: mix(dusk.hazeAmount, grade.hazeAmount, k),
    tint: mixHex(dusk.tint, grade.tint, k),
    tintAmount: mix(dusk.tintAmount, grade.tintAmount, k),
    valueScale: mix(dusk.valueScale, grade.valueScale, k),
  };
}

/** Values of the option uniforms in the post pass. Neutral options give all zeros. */
export function postOptionUniforms(options: PipelineOptions): { uNvg: number; uFlashCap: number } {
  return { uNvg: options.nvg ? 1 : 0, uFlashCap: options.flashCap ? FLASH_CAP_STEP : 0 };
}

/**
 * Values of the option uniforms in the upscale pass at integer scale `scale`. `uScanline` is
 * what the gap row is multiplied by: 1 (nothing) when the CRT look is off, and at x1, where a
 * scanline would be every row.
 */
export function upscaleOptionUniforms(
  options: PipelineOptions, scale: number,
): { uScanline: number; uVignetteBoost: number } {
  if (!options.crt) return { uScanline: 1, uVignetteBoost: 0 };
  const gap = SCANLINE_GAP[Math.min(SCANLINE_GAP.length - 1, Math.max(0, Math.floor(scale)))] ?? 1;
  return { uScanline: gap, uVignetteBoost: 0.14 };
}
